import React from 'react';
import { Box, Stack } from '@mui/material';
import { useQuery } from '@apollo/client';
import { useFormContext } from 'react-hook-form';
import { blueGrey } from '@mui/material/colors';
import Typography from '@mui/material/Typography';
import { SelectField, AutocompleteField } from 'components/form';
import { convertEnumToSelectOptions } from 'utils/selectOptionsHelper';
import { AddLocation, LocalShipping, PeopleAlt } from '@mui/icons-material';
import { BizOrIndividual, PaymentType, TruckType, Country } from '@trnsact/trnsact-shared-types';

import { dealSheetConfigurationApi } from '../../api';
import { DealSheetFormValues } from './types';

export const RulesSection = () => {
  const { control } = useFormContext<DealSheetFormValues>();

  const { data: countriesResponse, loading: countriesOptionsLoading } = useQuery(
    dealSheetConfigurationApi.getCountries
  );

  const { data: usCountryOptionsResponse, loading: usCountryOptionsLoading } = useQuery(
    dealSheetConfigurationApi.getRegions,
    {
      variables: { input: { country: Country.Us } },
    }
  );

  const { data: caCountryOptionsResponse, loading: caCountryOptionsLoading } = useQuery(
    dealSheetConfigurationApi.getRegions,
    {
      variables: { input: { country: Country.Ca } },
    }
  );

  return (
    <Stack gap="1rem">
      <Typography sx={{ marginTop: '0.5rem' }}>
        Create business rules by specifying one or more criteria. At least one field must be selected
      </Typography>

      <Stack gap="1rem">
        <Box
          sx={{
            gap: '0.3rem',
            display: 'flex',
            borderRadius: '0.5rem',
            padding: '0.5rem 1rem',
            backgroundColor: blueGrey['50'],
          }}
        >
          <PeopleAlt />

          <Typography sx={{ color: blueGrey['500'] }}>Customer Configuration</Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
          <AutocompleteField
            multiple
            size="small"
            control={control}
            name="customer.type"
            textFieldProps={{ variant: 'outlined', label: 'Customer type' }}
            options={[
              { label: 'Individual', value: BizOrIndividual.Individual },
              { label: 'Business', value: BizOrIndividual.Business },
            ]}
          />

          <AutocompleteField
            multiple
            size="small"
            control={control}
            name="customer.paymentType"
            options={convertEnumToSelectOptions(PaymentType)}
            textFieldProps={{ variant: 'outlined', label: 'Payment type' }}
          />

          <Box />
        </Box>
      </Stack>

      <Stack gap="1rem">
        <Box
          sx={{
            gap: '0.3rem',
            display: 'flex',
            borderRadius: '0.5rem',
            padding: '0.5rem 1rem',
            backgroundColor: blueGrey['50'],
          }}
        >
          <AddLocation />

          <Typography sx={{ color: blueGrey['500'] }}>Location Configuration</Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
          <SelectField
            name="address.country"
            label="Country"
            control={control}
            variant="outlined"
            isLoading={countriesOptionsLoading}
            options={countriesResponse?.dealSheets?.result?.list ?? []}
          />

          <SelectField name="address.zip" label="ZIP Code" control={control} variant="outlined" options={[]} />

          <Box />

          <SelectField
            label="State"
            control={control}
            variant="outlined"
            name="address.state"
            isLoading={usCountryOptionsLoading}
            options={usCountryOptionsResponse?.dealSheets?.result?.list ?? []}
          />

          <SelectField
            label="County"
            control={control}
            variant="outlined"
            name="address.county"
            isLoading={caCountryOptionsLoading}
            options={caCountryOptionsResponse?.dealSheets?.result?.list ?? []}
          />

          <SelectField name="address.city" label="City" control={control} variant="outlined" options={[]} />
        </Box>
      </Stack>

      <Stack gap="1rem">
        <Box
          sx={{
            gap: '0.3rem',
            display: 'flex',
            borderRadius: '0.5rem',
            padding: '0.5rem 1rem',
            backgroundColor: blueGrey['50'],
          }}
        >
          <LocalShipping />

          <Typography sx={{ color: blueGrey['500'] }}>Equipment Configuration</Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
          <AutocompleteField
            multiple
            size="small"
            control={control}
            name="equipment.type"
            options={convertEnumToSelectOptions(TruckType)}
            textFieldProps={{ variant: 'outlined', label: 'Equipment type' }}
          />

          <Box />

          <Box />
        </Box>
      </Stack>
    </Stack>
  );
};
